import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const colorCodes = {
  black: { value: 0, multiplier: 1, color: "#000000" },
  brown: { value: 1, multiplier: 10, tolerance: 1, color: "#8B4513" },
  red: { value: 2, multiplier: 100, tolerance: 2, color: "#FF0000" },
  orange: { value: 3, multiplier: 1000, color: "#FFA500" },
  yellow: { value: 4, multiplier: 10000, color: "#FFFF00" },
  green: { value: 5, multiplier: 100000, tolerance: 0.5, color: "#00FF00" },
  blue: { value: 6, multiplier: 1000000, tolerance: 0.25, color: "#0000FF" },
  violet: { value: 7, multiplier: 10000000, tolerance: 0.1, color: "#9400D3" },
  gray: { value: 8, multiplier: 100000000, tolerance: 0.05, color: "#808080" },
  white: { value: 9, multiplier: 1000000000, color: "#FFFFFF" },
  gold: { multiplier: 0.1, tolerance: 5, color: "#FFD700" },
  silver: { multiplier: 0.01, tolerance: 10, color: "#C0C0C0" },
  none: { tolerance: 20, color: "transparent" },
};

export default function ResistorColorCalculator() {
  const [band1, setBand1] = useState("brown");
  const [band2, setBand2] = useState("black");
  const [band3, setBand3] = useState("red");
  const [band4, setBand4] = useState("gold");

  const calculateResistance = () => {
    const band1Data = colorCodes[band1 as keyof typeof colorCodes];
    const band2Data = colorCodes[band2 as keyof typeof colorCodes];
    const band3Data = colorCodes[band3 as keyof typeof colorCodes];
    const band4Data = colorCodes[band4 as keyof typeof colorCodes];

    const digit1 = 'value' in band1Data ? band1Data.value : undefined;
    const digit2 = 'value' in band2Data ? band2Data.value : undefined;
    const multiplier = 'multiplier' in band3Data ? band3Data.multiplier : undefined;
    const tolerance = 'tolerance' in band4Data ? band4Data.tolerance : undefined;

    if (digit1 === undefined || digit2 === undefined || multiplier === undefined || tolerance === undefined) return null;

    const resistance = (digit1 * 10 + digit2) * multiplier;
    return { resistance, tolerance };
  };

  const result = calculateResistance();

  const formatResistance = (value: number) => {
    if (value >= 1000000) return `${(value / 1000000).toFixed(2)} MΩ`;
    if (value >= 1000) return `${(value / 1000).toFixed(2)} kΩ`;
    return `${value.toFixed(2)} Ω`;
  };

  const digitColors = Object.entries(colorCodes).filter(([_, v]) => 'value' in v && v.value !== undefined);
  const multiplierColors = Object.entries(colorCodes).filter(([_, v]) => 'multiplier' in v && v.multiplier !== undefined);
  const toleranceColors = Object.entries(colorCodes).filter(([_, v]) => 'tolerance' in v && v.tolerance !== undefined);

  return (
    <PageLayout calculatorName="Resistor Color Code Calculator" calculatorPath="/calculator/resistor-color">
      <div className="max-w-2xl mx-auto">
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle>4-Band Resistor Color Code</CardTitle>
            <CardDescription>
              Select the color of each band to calculate resistance
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Band 1 (First Digit)</Label>
                <Select value={band1} onValueChange={setBand1}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {digitColors.map(([name, data]) => {
                      const value = 'value' in data ? data.value : 0;
                      return (
                        <SelectItem key={name} value={name}>
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-4 h-4 rounded border"
                              style={{ 
                                backgroundColor: data.color,
                                border: data.color === '#FFFFFF' ? '1px solid #ccc' : 'none'
                              }}
                            />
                            <span className="capitalize">{name} ({value})</span>
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Band 2 (Second Digit)</Label>
                <Select value={band2} onValueChange={setBand2}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {digitColors.map(([name, data]) => {
                      const value = 'value' in data ? data.value : 0;
                      return (
                        <SelectItem key={name} value={name}>
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-4 h-4 rounded border"
                              style={{ 
                                backgroundColor: data.color,
                                border: data.color === '#FFFFFF' ? '1px solid #ccc' : 'none'
                              }}
                            />
                            <span className="capitalize">{name} ({value})</span>
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Band 3 (Multiplier)</Label>
                <Select value={band3} onValueChange={setBand3}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {multiplierColors.map(([name, data]) => {
                      const multiplier = 'multiplier' in data ? data.multiplier : 1;
                      return (
                        <SelectItem key={name} value={name}>
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-4 h-4 rounded border"
                              style={{ 
                                backgroundColor: data.color,
                                border: data.color === '#FFFFFF' || data.color === '#FFD700' ? '1px solid #ccc' : 'none'
                              }}
                            />
                            <span className="capitalize">{name} (×{multiplier})</span>
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Band 4 (Tolerance)</Label>
                <Select value={band4} onValueChange={setBand4}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {toleranceColors.map(([name, data]) => {
                      const tolerance = 'tolerance' in data ? data.tolerance : 0;
                      return (
                        <SelectItem key={name} value={name}>
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-4 h-4 rounded border"
                              style={{ 
                                backgroundColor: data.color,
                                border: data.color === '#FFD700' || data.color === 'transparent' ? '1px solid #ccc' : 'none'
                              }}
                            />
                            <span className="capitalize">{name} (±{tolerance}%)</span>
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {result && (
              <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
                <CardHeader>
                  <CardTitle className="text-xl">Result</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold text-primary">
                    {formatResistance(result.resistance)}
                  </p>
                  <p className="text-sm text-muted-foreground mt-2">
                    Tolerance: ±{result.tolerance}%
                  </p>
                </CardContent>
              </Card>
            )}

            <Card className="bg-muted/50">
              <CardHeader>
                <CardTitle className="text-lg">How to use</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm text-muted-foreground">
                <p>1. Select the color of the first band (first digit)</p>
                <p>2. Select the color of the second band (second digit)</p>
                <p>3. Select the color of the third band (multiplier)</p>
                <p>4. Select the color of the fourth band (tolerance)</p>
                <p className="pt-2 font-medium text-foreground">
                  Example: Brown-Black-Red-Gold = 10 × 100 = 1,000Ω ±5%
                </p>
              </CardContent>
            </Card>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
}
